import * as React from 'react';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {FileObject, FileType, getImageByPreview} from 'api/library';
import FilePicker from 'components/FilePicker';
import {IconPreview} from 'icons';
import {transition} from 'helpers/css';
import outline from 'helpers/outline';
import useModal from 'hooks/use-modal';
import {PreviewObject} from 'types';

export interface PreviewPickerProps {
  preview?: PreviewObject;
  onChange?(file: FileObject): void;
}

export interface PatchObject {
  uri: FileObject['uri'] | undefined;
  isImageSelected: boolean;
}

export const Container = styled.button<PatchObject>`
  position: relative;
  max-width: 300px;
  width: 100%;
  height: 170px;
  border-radius: 5px;
  overflow: hidden;
  ${transition()};
  background-image: ${({uri}) => uri && `url(${uri})`};
  background-position: center;
  background-size: cover;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    ${transition()};
    border: 2px dashed ${({isImageSelected, theme}) => isImageSelected ? 'transparent' : `
      ${transparentize(.85, theme.colors.white)};
    `};
  }
  
  &:hover:before {
    background: ${({theme}) => `
      ${transparentize(.8, theme.colors.black)};
    `};
    border-color: ${({isImageSelected, theme}) => {
      return isImageSelected
        ? `${transparentize(.4, theme.variables.color)}`
        : `${transparentize(.6, theme.variables.color)}`
    }};
  }
  
  &:active:before,
  &:focus-visible:before {
    background: ${({theme}) => `
      ${transparentize(.4, theme.colors.black)};
    `};
    border-color: ${({theme}) => theme.colors.primary};
  }
  
  &:focus-visible {
    ${outline(8, 2)};
  }
`;

export const IconContainer = styled.div`
  justify-content: center;
  align-items: center;
  display: grid;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  
  ${transition()};
  color: ${({theme}) => `
    ${transparentize(.6, theme.variables.color)};
  `};
  
  svg {
    fill: currentColor;
  }
`;

/**
 * The preview picker component
 * @param {PreviewPickerProps} props
 * @return {React.ReactElement<PreviewPickerProps>}
 * @constructor
 */
export default function PreviewPicker(
    props: PreviewPickerProps,
): React.ReactElement<PreviewPickerProps> {
  const {
    isModalVisible,
    showModal,
    hideModal,
  } = useModal();

  /**
   * Get the patch
   * @return {PatchObject}
   */
  function getPatch(): PatchObject['uri'] {
    return getImageByPreview(props.preview);
  }

  /**
   * check if the image is selected
   * @return {boolean}
   */
  function checkIsImageSelected(): boolean {
    return !Boolean(
      getImageByPreview(props.preview).includes('/images/not-found')
    )
  }

  /**
   * Handle select file
   * @param {FileObject[]} attached
   */
  function handleSelect(attached: FileObject[]) {
    props.onChange && props.onChange(attached[0]);
    hideModal && hideModal();
  }
  /**
   * Handle blur modal
   */
  function handleBlur(): void {
    hideModal && hideModal();
  }
  return (
    <>
      <Container
        type="button"
        id="preview-picker-container"
        uri={getPatch()}
        isImageSelected={checkIsImageSelected()}
        onClick={showModal}
      >
        {!getPatch() && (
          <IconContainer>
            <IconPreview />
          </IconContainer>
        )}
      </Container>
      {isModalVisible && (
        <FilePicker
          type={FileType.Image}
          tabbarProps={{
            param: 'pickerTab',
          }}
          onSelect={handleSelect}
          onBlur={handleBlur}
          upload
        />
      )}
    </>
  );
}
